/**
 * CommissionsView — Agent's personal earnings tracker
 *
 * Shows:
 * - YTD earnings summary (paid + pending)
 * - Deal-by-deal commission breakdown
 * - Monthly earnings chart
 * - Estimated tax liability (1099 agents)
 */
import { useState } from 'react';
import { DollarSign, TrendingUp, Calendar, FileText, AlertCircle, CheckCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { theme } from '../../lib/theme';
import EmptyState from '../common/EmptyState';
import LoadingSpinner from '../common/LoadingSpinner';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function CommissionsView({ user, demoMode, showToast }) {
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all'); // all, paid, pending

  const agentName = user?.user_metadata?.full_name || 'Agent';

  // Demo commission data
  const commissions = demoMode ? [
    {
      id: 1, deal: '789 Washington Ave', client: 'Frank Moretti', type: 'Seller',
      closeDate: '2026-03-28', salePrice: 425000, grossCommission: 12750,
      split: 70, agentPayout: 8925, brokeragePayout: 3825,
      status: 'paid', paidDate: '2026-04-02',
    },
    {
      id: 2, deal: '456 Cherry Hill Rd', client: 'Thompson Family', type: 'Buyer',
      closeDate: '2026-04-18', salePrice: 385000, grossCommission: 11550,
      split: 70, agentPayout: 8085, brokeragePayout: 3465,
      status: 'pending', paidDate: null,
    },
    {
      id: 3, deal: '55 Bancroft Lane', client: 'Patricia Keene', type: 'Buyer',
      closeDate: '2026-02-14', salePrice: 310000, grossCommission: 9300,
      split: 70, agentPayout: 6510, brokeragePayout: 2790,
      status: 'paid', paidDate: '2026-02-21',
    },
    {
      id: 4, deal: '123 Haddonfield Rd', client: 'Nguyen Family', type: 'Buyer',
      closeDate: '2026-05-10', salePrice: 520000, grossCommission: 15600,
      split: 70, agentPayout: 10920, brokeragePayout: 4680,
      status: 'pending', paidDate: null,
    },
    {
      id: 5, deal: '201 Maple Ct', client: 'Jim DeAngelo', type: 'Seller',
      closeDate: '2026-01-30', salePrice: 275000, grossCommission: 8250,
      split: 70, agentPayout: 5775, brokeragePayout: 2475,
      status: 'paid', paidDate: '2026-02-05',
    },
  ] : [];

  const filtered = commissions.filter(c => activeFilter === 'all' || c.status === activeFilter);
  const totalPaid = commissions.filter(c => c.status === 'paid').reduce((s, c) => s + c.agentPayout, 0);
  const totalPending = commissions.filter(c => c.status === 'pending').reduce((s, c) => s + c.agentPayout, 0);
  const totalYTD = totalPaid + totalPending;
  const dealsClosed = commissions.filter(c => c.status === 'paid').length;
  const avgCommission = dealsClosed > 0 ? Math.round(totalPaid / dealsClosed) : 0;

  // Estimated quarterly tax (1099: ~30% self-employment + income tax)
  const estimatedTax = Math.round(totalYTD * 0.30);

  // Monthly breakdown for chart
  const monthlyData = MONTHS.map((_, i) => {
    return commissions
      .filter(c => c.status === 'paid' && new Date(c.paidDate).getMonth() === i)
      .reduce((s, c) => s + c.agentPayout, 0);
  });
  const maxMonthly = Math.max(...monthlyData, 1);

  const fmt = (n) => '$' + n.toLocaleString();
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

  return (
    <div style={{ padding: '20px 24px', maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.colors.text }}>My Earnings</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.colors.textLight }}>
          Commission tracker for {agentName} — {new Date().getFullYear()} YTD
        </p>
      </div>

      {/* Summary Cards */}
      <div data-tour="earnings-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { icon: DollarSign, label: 'YTD Earnings', value: fmt(totalYTD), color: theme.colors.primary, sub: `${commissions.length} deals` },
          { icon: CheckCircle, label: 'Paid', value: fmt(totalPaid), color: theme.colors.success, sub: `${dealsClosed} closed` },
          { icon: Clock, label: 'Pending', value: fmt(totalPending), color: theme.colors.accent, sub: `${commissions.length - dealsClosed} in pipeline` },
          { icon: TrendingUp, label: 'Avg per Deal', value: fmt(avgCommission), color: theme.colors.info, sub: dealsClosed > 0 ? `From ${dealsClosed} closings` : 'No closings yet' },
        ].map((s, i) => (
          <div key={i} style={{
            background: 'white', borderRadius: 14, padding: '16px 18px',
            boxShadow: theme.shadow.sm, border: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: `${s.color}12`,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <s.icon size={16} color={s.color} />
              </div>
              <span style={{ fontSize: 12, color: theme.colors.textLight, fontWeight: 500 }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Tax estimate banner */}
      {totalYTD > 0 && (
        <div style={{
          background: `${theme.colors.warning}10`, border: `1px solid ${theme.colors.warning}35`,
          borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertCircle size={18} color={theme.colors.warning} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>Estimated Tax Liability (1099)</div>
              <div style={{ fontSize: 11, color: theme.colors.textLight }}>~30% for self-employment + federal/state income tax. Consult your CPA.</div>
            </div>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.colors.warning }}>{fmt(estimatedTax)}</div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Monthly Earnings Chart */}
        <div data-tour="earnings-chart" style={{
          background: 'white', borderRadius: 14, padding: 18,
          border: `1px solid ${theme.colors.border}`, boxShadow: theme.shadow.sm,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
            <Calendar size={16} color={theme.colors.primary} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text, margin: 0 }}>Monthly Earnings</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120, paddingBottom: 4 }}>
            {monthlyData.map((val, i) => {
              const height = val > 0 ? Math.max((val / maxMonthly) * 100, 4) : 2;
              const currentMonth = new Date().getMonth();
              return (
                <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{
                    height, borderRadius: '4px 4px 0 0',
                    background: val > 0
                      ? i <= currentMonth ? theme.colors.success : `${theme.colors.success}40`
                      : theme.colors.border,
                    transition: 'height 0.3s ease',
                    margin: '0 auto',
                    width: '80%',
                  }} />
                  <div style={{ fontSize: 9, color: i === currentMonth ? theme.colors.primary : theme.colors.textLight, marginTop: 4, fontWeight: i === currentMonth ? 700 : 400 }}>
                    {MONTHS[i]}
                  </div>
                  {val > 0 && (
                    <div style={{ fontSize: 8, color: theme.colors.textLight }}>
                      ${Math.round(val / 1000)}k
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Split Summary */}
        <div style={{
          background: 'white', borderRadius: 14, padding: 18,
          border: `1px solid ${theme.colors.border}`, boxShadow: theme.shadow.sm,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
            <TrendingUp size={16} color={theme.colors.accent} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text, margin: 0 }}>Commission Breakdown</h3>
          </div>
          <div style={{ fontSize: 13, lineHeight: 2.2 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.colors.textLight }}>Total Gross Commission:</span>
              <span style={{ fontWeight: 600, color: theme.colors.text }}>{fmt(commissions.reduce((s, c) => s + c.grossCommission, 0))}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.colors.textLight }}>Your Split ({commissions[0]?.split || 70}%):</span>
              <span style={{ fontWeight: 700, color: theme.colors.success }}>{fmt(totalYTD)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.colors.textLight }}>Brokerage Portion:</span>
              <span style={{ fontWeight: 600, color: theme.colors.textLight }}>{fmt(commissions.reduce((s, c) => s + c.brokeragePayout, 0))}</span>
            </div>
            <div style={{ borderTop: `1px solid ${theme.colors.border}`, marginTop: 4, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.colors.textLight }}>Total Volume:</span>
              <span style={{ fontWeight: 700, color: theme.colors.primary }}>{fmt(commissions.reduce((s, c) => s + c.salePrice, 0))}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Deal List */}
      <div data-tour="earnings-deals" style={{
        background: 'white', borderRadius: 14, boxShadow: theme.shadow.sm,
        border: `1px solid ${theme.colors.border}`, overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${theme.colors.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={16} color={theme.colors.primary} />
            <span style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>Deal History</span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {['all', 'paid', 'pending'].map(f => (
              <button key={f} onClick={() => setActiveFilter(f)}
                style={{ padding: '5px 12px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  background: activeFilter === f ? theme.colors.primary : `${theme.colors.primary}10`,
                  color: activeFilter === f ? 'white' : theme.colors.primary, textTransform: 'capitalize' }}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState type="commissions" />
        ) : (
          filtered.map((c, i) => {
            const isExpanded = expandedDeal === c.id;
            return (
              <div key={c.id} style={{ borderBottom: i < filtered.length - 1 ? `1px solid ${theme.colors.border}` : 'none' }}>
                <div
                  onClick={() => setExpandedDeal(isExpanded ? null : c.id)}
                  style={{
                    padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: c.status === 'paid' ? `${theme.colors.success}12` : `${theme.colors.accent}12`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {c.status === 'paid' ? <CheckCircle size={16} color={theme.colors.success} /> : <Clock size={16} color={theme.colors.accent} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>{c.deal}</div>
                      <div style={{ fontSize: 12, color: theme.colors.textLight }}>
                        {c.client} · {c.type} · {formatDate(c.closeDate)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: c.status === 'paid' ? theme.colors.success : theme.colors.accent }}>
                        {fmt(c.agentPayout)}
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                        background: c.status === 'paid' ? `${theme.colors.success}15` : `${theme.colors.accent}15`,
                        color: c.status === 'paid' ? theme.colors.success : theme.colors.accent,
                        textTransform: 'uppercase',
                      }}>
                        {c.status}
                      </span>
                    </div>
                    {isExpanded ? <ChevronUp size={16} color={theme.colors.textLight} /> : <ChevronDown size={16} color={theme.colors.textLight} />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{
                    padding: '0 20px 16px 68px', fontSize: 12, color: theme.colors.text, lineHeight: 2,
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                      <div><span style={{ color: theme.colors.textLight }}>Sale Price:</span> {fmt(c.salePrice)}</div>
                      <div><span style={{ color: theme.colors.textLight }}>Gross Commission:</span> {fmt(c.grossCommission)}</div>
                      <div><span style={{ color: theme.colors.textLight }}>Your Split:</span> {c.split}% = {fmt(c.agentPayout)}</div>
                      <div><span style={{ color: theme.colors.textLight }}>Brokerage:</span> {100 - c.split}% = {fmt(c.brokeragePayout)}</div>
                      <div><span style={{ color: theme.colors.textLight }}>Close Date:</span> {formatDate(c.closeDate)}</div>
                      <div><span style={{ color: theme.colors.textLight }}>Paid Date:</span> {formatDate(c.paidDate)}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
